import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Target, Plus, Trash2, AlertTriangle, CheckCircle, 
  TrendingUp, DollarSign, Calendar, Loader2, RefreshCw,
  PieChart, AlertCircle, Filter, BarChart3, Layers
} from "lucide-react";
import { formatCurrency } from "@/lib/config";
import { toast } from "sonner";
import api from "@/lib/api";

// Couleurs par niveau de profit
const NIVEAU_COLORS = {
  "Brut": { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300" },
  "Marge": { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300" },
  "Net": { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300" },
  "Hors_Résultat": { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300" },
  "Non défini": { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-300" }
};

export const GestionBudgets = () => {
  const [loading, setLoading] = useState(true);
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesByNiveau, setCategoriesByNiveau] = useState({});
  const [yearProgress, setYearProgress] = useState(0);
  const [alertsCount, setAlertsCount] = useState(0);
  const [totalsByNiveau, setTotalsByNiveau] = useState({});
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterNiveau, setFilterNiveau] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const [newBudget, setNewBudget] = useState({
    category: "",
    annual_budget: 0,
    type_depense: "",
    nature_depense: "",
    niveau_profit: "",
    notes: ""
  });

  useEffect(() => {
    loadData();
  }, [selectedYear, filterNiveau]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (filterNiveau && filterNiveau !== "all") {
        params.append("niveau_profit", filterNiveau);
      }
      
      const [budgetsRes, categoriesRes] = await Promise.all([
        axios.get(`${api.baseUrl}/budgets?${params}`),
        axios.get(`${api.baseUrl}/budgets/categories`)
      ]);
      
      const budgetsData = budgetsRes.data;
      const categoriesData = categoriesRes.data;
      
      setBudgets(budgetsData.budgets || []);
      setYearProgress(budgetsData.year_progress_pct || 0);
      setAlertsCount(budgetsData.alerts_count || 0);
      setTotalsByNiveau(budgetsData.totals_by_niveau_profit || {});
      setCategories(categoriesData.categories || []);
      setCategoriesByNiveau(categoriesData.by_niveau_profit || {});
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryName) => {
    const cat = categories.find(c => c.categorie === categoryName);
    if (cat) {
      setNewBudget({
        ...newBudget,
        category: categoryName,
        type_depense: cat.type_depense || "",
        nature_depense: cat.nature_depense || "",
        niveau_profit: cat.niveau_profit || ""
      });
    } else {
      setNewBudget({ ...newBudget, category: categoryName });
    }
  };

  const saveBudget = async () => {
    if (!newBudget.category || newBudget.annual_budget <= 0) {
      toast.error("Veuillez remplir tous les champs requis");
      return;
    }
    
    try {
      const response = await axios.post(`${api.baseUrl}/budgets`, {
          ...newBudget,
          year: selectedYear
        });
      
      if (response.status === 200) {
        toast.success("Budget créé avec succès");
        setIsDialogOpen(false);
        setNewBudget({ category: "", annual_budget: 0, type_depense: "", nature_depense: "", niveau_profit: "", notes: "" });
        loadData();
      }
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const deleteBudget = async (budgetId) => {
    if (!confirm("Supprimer ce budget?")) return;
    
    try {
      await axios.delete(`${api.baseUrl}/budgets/${budgetId}`);
      toast.success("Budget supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getNiveauBadge = (niveau) => {
    const colors = NIVEAU_COLORS[niveau] || NIVEAU_COLORS["Non défini"];
    return (
      <Badge className={`${colors.bg} ${colors.text} border ${colors.border}`}>
        {niveau === "Hors_Résultat" ? "Hors Résultat" : niveau}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestion-budgets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestion des Budgets</h1>
          <p className="text-slate-500">Suivez vos budgets par catégorie et leur impact sur la rentabilité</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau budget
          </Button>
        </div>
      </div>

      {/* Résumé global */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-slate-100">
                <Calendar className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Année écoulée</p>
                <p className="text-xl font-bold">{yearProgress.toFixed(0)}%</p>
              </div>
            </div>
            <Progress value={yearProgress} className="mt-3 h-2" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs text-slate-500">Budget total</p>
            <p className="text-xl font-bold">{formatCurrency(budgets.reduce((s, b) => s + (b.annual_budget || 0), 0))}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs text-slate-500">Dépensé (HT)</p>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(budgets.reduce((s, b) => s + (b.spent || 0), 0))}</p>
          </CardContent>
        </Card>
        <Card className={alertsCount > 0 ? "border-red-300" : ""}>
          <CardContent className="pt-6">
            <p className="text-xs text-slate-500">Alertes</p>
            <p className="text-xl font-bold text-red-600">{alertsCount}</p>
            <p className="text-xs text-slate-400">Budget(s) en dépassement</p>
          </CardContent>
        </Card>
      </div>

      {/* Liste des budgets */}
      {budgets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Target className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium mb-2">Aucun budget défini</h3>
            <p className="mb-4">Créez des budgets pour suivre vos dépenses par catégorie</p>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Créer un budget
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="cards" className="w-full">
          <TabsList>
            <TabsTrigger value="cards">Vue cartes</TabsTrigger>
            <TabsTrigger value="table">Vue tableau</TabsTrigger>
          </TabsList>
          
          <TabsContent value="cards">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {budgets.map(budget => (
                <BudgetCard 
                  key={budget.id} 
                  budget={budget} 
                  yearProgress={yearProgress}
                  onDelete={() => deleteBudget(budget.id)}
                />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="table">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium text-slate-600">Catégorie</th>
                        <th className="text-left p-3 font-medium text-slate-600">Niveau Profit</th>
                        <th className="text-right p-3 font-medium text-slate-600">Budget</th>
                        <th className="text-right p-3 font-medium text-slate-600">Dépensé</th>
                        <th className="text-right p-3 font-medium text-slate-600">Restant</th>
                        <th className="text-center p-3 font-medium text-slate-600">%</th>
                        <th className="text-center p-3 font-medium text-slate-600">Statut</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {budgets.map(b => (
                        <tr key={b.id} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium">{b.category}</td>
                          <td className="p-3">{getNiveauBadge(b.niveau_profit)}</td>
                          <td className="p-3 text-right">{formatCurrency(b.annual_budget)}</td>
                          <td className="p-3 text-right">{formatCurrency(b.spent)}</td>
                          <td className={`p-3 text-right ${b.remaining < 0 ? "text-red-600 font-medium" : ""}`}>
                            {formatCurrency(b.remaining)}
                          </td>
                          <td className="p-3 text-center">
                            <span className={`font-medium ${
                              b.budget_used_pct > 100 ? "text-red-600" :
                              b.budget_used_pct > b.year_progress_pct ? "text-amber-600" : ""
                            }`}>
                              {b.budget_used_pct.toFixed(0)}%
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            {b.status === "critical" && <Badge className="bg-red-500">Dépassé</Badge>}
                            {b.status === "warning" && <Badge className="bg-amber-500">Attention</Badge>}
                            {b.status === "caution" && <Badge className="bg-yellow-500">Surveiller</Badge>}
                            {b.status === "ok" && <Badge className="bg-emerald-500">OK</Badge>}
                          </td>
                          <td className="p-3">
                            <Button variant="ghost" size="sm" onClick={() => deleteBudget(b.id)}>
                              <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Dialog création budget */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" aria-describedby="budget-dialog-description">
          <DialogHeader>
            <DialogTitle>Nouveau budget</DialogTitle>
            <p id="budget-dialog-description" className="text-sm text-slate-500">
              Sélectionnez une catégorie pour voir automatiquement son type et niveau de profit
            </p>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Catégorie *</Label>
              <Select 
                value={newBudget.category} 
                onValueChange={handleCategorySelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une catégorie..." />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {Object.entries(categoriesByNiveau).map(([niveau, cats]) => (
                    <div key={niveau}>
                      <div className={`px-2 py-1 text-xs font-semibold ${NIVEAU_COLORS[niveau]?.text || "text-slate-600"} ${NIVEAU_COLORS[niveau]?.bg || "bg-slate-100"} sticky top-0`}>
                        {niveau === "Hors_Résultat" ? "Hors Résultat" : niveau}
                      </div>
                      {cats.map(cat => (
                        <SelectItem key={cat.categorie} value={cat.categorie}>
                          {cat.categorie}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newBudget.category && (
              <div className="grid grid-cols-3 gap-2 p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Type</p>
                  <p className="text-sm font-medium">{newBudget.type_depense || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Nature</p>
                  <p className="text-sm font-medium">{newBudget.nature_depense || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Niveau Profit</p>
                  {newBudget.niveau_profit && getNiveauBadge(newBudget.niveau_profit)}
                </div>
              </div>
            )}
            
            <div>
              <Label>Budget annuel ($) *</Label>
              <Input
                type="number"
                value={newBudget.annual_budget}
                onChange={(e) => setNewBudget({...newBudget, annual_budget: parseFloat(e.target.value) || 0})}
                placeholder="Ex: 3000"
              />
            </div>
            
            <div>
              <Label>Notes (optionnel)</Label>
              <Input
                value={newBudget.notes}
                onChange={(e) => setNewBudget({...newBudget, notes: e.target.value})}
                placeholder="Ex: Budget pour les repas d'équipe"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveBudget}>
              Créer le budget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Composant carte de budget
const BudgetCard = ({ budget, yearProgress, onDelete }) => {
  const isOverPace = budget.budget_used_pct > yearProgress;
  const isCritical = budget.status === "critical";
  const isWarning = budget.status === "warning";
  const niveauColors = NIVEAU_COLORS[budget.niveau_profit] || NIVEAU_COLORS["Non défini"];
  
  return (
    <Card className={`
      transition-all
      ${isCritical ? "border-red-300 bg-red-50" : ""}
      ${isWarning ? "border-amber-300 bg-amber-50" : ""}
    `}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              isCritical ? "bg-red-200" : 
              isWarning ? "bg-amber-200" : 
              niveauColors.bg
            }`}>
              <PieChart className={`w-5 h-5 ${
                isCritical ? "text-red-600" : 
                isWarning ? "text-amber-600" : 
                niveauColors.text
              }`} />
            </div>
            <div>
              <CardTitle className="text-lg">{budget.category}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`text-xs ${niveauColors.bg} ${niveauColors.text} border ${niveauColors.border}`}>
                  {budget.niveau_profit === "Hors_Résultat" ? "Hors Résultat" : budget.niveau_profit || "Non défini"}
                </Badge>
                {budget.type_depense && (
                  <span className="text-xs text-slate-500">{budget.type_depense}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {budget.status === "critical" && (
              <Badge className="bg-red-500">
                <AlertCircle className="w-3 h-3 mr-1" />
                Dépassé
              </Badge>
            )}
            {budget.status === "warning" && (
              <Badge className="bg-amber-500">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Attention
              </Badge>
            )}
            {budget.status === "ok" && (
              <Badge className="bg-emerald-500">
                <CheckCircle className="w-3 h-3 mr-1" />
                OK
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Alerte si nécessaire */}
        {budget.alert_message && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            isCritical ? "bg-red-100 text-red-800" : 
            isWarning ? "bg-amber-100 text-amber-800" : 
            "bg-yellow-100 text-yellow-800"
          }`}>
            {budget.alert_message}
          </div>
        )}
        
        {/* Montants */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500">Budget</p>
            <p className="font-bold text-slate-900">{formatCurrency(budget.annual_budget)}</p>
          </div>
          <div className={`text-center p-2 rounded-lg ${
            isCritical ? "bg-red-100" : isWarning ? "bg-amber-100" : "bg-blue-50"
          }`}>
            <p className="text-xs text-slate-500">Dépensé</p>
            <p className={`font-bold ${
              isCritical ? "text-red-600" : isWarning ? "text-amber-600" : "text-blue-600"
            }`}>
              {formatCurrency(budget.spent)}
            </p>
          </div>
          <div className={`text-center p-2 rounded-lg ${
            budget.remaining < 0 ? "bg-red-100" : "bg-emerald-50"
          }`}>
            <p className="text-xs text-slate-500">Restant</p>
            <p className={`font-bold ${
              budget.remaining < 0 ? "text-red-600" : "text-emerald-600"
            }`}>
              {formatCurrency(budget.remaining)}
            </p>
          </div>
        </div>
        
        {/* Barre de progression */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-slate-500">
            <span>Consommation du budget</span>
            <span className={`font-medium ${
              isCritical ? "text-red-600" : isWarning ? "text-amber-600" : ""
            }`}>
              {budget.budget_used_pct.toFixed(0)}%
            </span>
          </div>
          
          <div className="relative">
            <Progress 
              value={Math.min(100, budget.budget_used_pct)} 
              className={`h-3 ${
                isCritical ? "[&>div]:bg-red-500" : 
                isWarning ? "[&>div]:bg-amber-500" : 
                ""
              }`}
            />
            
            {/* Marqueur de progression de l'année */}
            <div 
              className="absolute top-0 h-3 w-0.5 bg-slate-800 z-10"
              style={{ left: `${Math.min(100, yearProgress)}%` }}
              title={`${yearProgress.toFixed(0)}% de l'année écoulée`}
            />
          </div>
          
          <div className="flex justify-between text-xs">
            <span className="text-slate-400">0%</span>
            <span className="text-slate-500">
              📅 {yearProgress.toFixed(0)}% de l'année
            </span>
            <span className="text-slate-400">100%</span>
          </div>
        </div>
        
        {/* Comparaison visuelle */}
        {isOverPace && !isCritical && (
          <div className="mt-3 flex items-center gap-2 text-xs text-amber-700">
            <TrendingUp className="w-4 h-4" />
            <span>
              Rythme de dépense: {(budget.budget_used_pct / yearProgress * 100 - 100).toFixed(0)}% au-dessus du prévu
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GestionBudgets;
